import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import { adminConfig } from 'src/environments/environment';
import { IDeliveryCoordinates } from 'src/app/common/models/deliveryCoordinates.interface';
import { conditionallyLog } from 'src/app/common/consoleLogHelper';
import { PolygonType } from './polygon-type.enum';
import { PolygonHelpers } from 'src/app/common/polygonHelpers';
import MeasureTool from 'measuretool-googlemaps-v3/dist/gmaps-measuretool.umd.js';
import { StoreHandoffService } from 'src/app/services/store-handoff.service';
import { FacilityManager } from 'src/app/managers/facility.manager';
import { ZipCodeOverlayManager } from './zipcode-overlay-manager';
import { MovPolygon } from '../../../common/models/movPolygon';
import { DeliveryMapChangeKinds, DeliveryMapChangeEvent } from './models/delivery-map-change-event.model';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';

import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { MatButton } from '@angular/material/button';
import { MultiFacilitySelectionComponent } from '../multi-facility-selection/multi-facility-selection.component';
import { HandoffInfo } from '@src/app/common/models/handoffInfo';
import { StoreMapData } from './models/storeData';

@Component({
  selector: 'app-delivery-map-component',
  templateUrl: './delivery-map.component.html',
  styleUrls: ['./delivery-map.component.scss'],
  standalone: true,
  imports: [
    MatSlideToggle,
    FormsModule,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatButton,
    MultiFacilitySelectionComponent,
  ],
})
export class DeliveryMapComponent implements OnInit {
  private zipOverlayManager!: ZipCodeOverlayManager;
  private pendingOperations: (() => void)[] = [];

  private readonly STANDARD_COLOR = '#FF0000';
  private readonly EXTENDED_COLOR = '#1100b1';
  private readonly MOV_COLOR = '#28a745';

  private readonly POLYGON_STYLE: Record<PolygonType, { color: string; zIndex: number }> = {
    [PolygonType.Standard]: { color: this.STANDARD_COLOR, zIndex: 2 },
    [PolygonType.Extended]: { color: this.EXTENDED_COLOR, zIndex: 1 },
    [PolygonType.Mov]: { color: this.MOV_COLOR, zIndex: 3 },
  };

  private movControlDiv?: any;
  @ViewChild('map', { static: false }) public mapElement: any;

  @Input({ required: true }) public saveDisabled!: boolean;
  @Input({ required: true }) public extendedDeliveryAreaEnabled!: boolean;

  @Input() public zoom = 12;
  @Input() public startingLat = 43.106444;
  @Input() public startingLng = -77.543754;
  @Input() public mapTypeId = 'roadmap';

  @Output() public saveButtonClicked = new EventEmitter<void>();
  @Output() public deliveryMapChanged = new EventEmitter<DeliveryMapChangeEvent>();

  public zipcodeOverlayEnabled = false;
  public zipcodeLabelsEnabled = true;
  public showMultipleDeliveryAreasEnabled = false;

  public map: google.maps.Map | undefined;
  public standardPolygon: google.maps.Polygon | undefined;
  public extendedPolygon: google.maps.Polygon | undefined;
  public otherStores: Map<number, StoreMapData> = new Map();
  private readonly movPolygons = new Map<
    string,
    { polygon: google.maps.Polygon; lastCoords: IDeliveryCoordinates[] }
  >();

  public facilityCoordinates: IDeliveryCoordinates | undefined;
  public facilityMarker: google.maps.marker.AdvancedMarkerElement | undefined;

  public lastStandardCoords: IDeliveryCoordinates[] = [];
  public lastExtendedCoords: IDeliveryCoordinates[] = [];

  public showResetPolygon = adminConfig.showResetPolygon;

  public measureTool: any;
  public editModeEnabled = false;

  constructor(
    private readonly toastr: ToastrService,
    private readonly storeHandoffService: StoreHandoffService,
    private readonly facilityManager: FacilityManager,
  ) {}

  private queueOrExecute(operation: () => void): void {
    if (!this.map) {
      this.pendingOperations.push(operation);
      return;
    }
    operation();
  }

  public ngOnInit(): void {
    setOptions({
      key: adminConfig.googleMapsApiKey,
      v: 'weekly', // google recommends using the weekly channel
    });

    Promise.all([importLibrary('maps'), importLibrary('marker')]).then(() => this.initializeDeliveryAreaMap());
  }

  public toggleExtendedDeliveryArea(): void {
    conditionallyLog('toggling extended delivery area enabled: ', this.extendedDeliveryAreaEnabled);
    this.extendedPolygon!.setMap(this.extendedDeliveryAreaEnabled ? this.map! : null);
    this.deliveryMapChanged.emit({ kind: 'extended-enabled', enabled: this.extendedDeliveryAreaEnabled });
    this.updateMovControlVisibility();
  }

  public toggleOtherStoreDeliveryAreas(): void {
    conditionallyLog('toggling multiple delivery areas enabled: ', this.showMultipleDeliveryAreasEnabled);
    this.otherStores.forEach((store) => {
      this.toggleStoreMapDataVisibility(store, this.showMultipleDeliveryAreasEnabled);
    });
    this.updateMovControlVisibility();
  }

  public renderSelectedStorePolygons(storesSelected: HandoffInfo[]): void {
    storesSelected = storesSelected.filter(
      (store) => store.facilityId.toString() != this.facilityManager.getCurrentFacility(),
    );
    const selectedIds = new Set(storesSelected.map((s) => s.facilityId));

    for (const store of storesSelected) {
      if (this.otherStores.has(store.facilityId) === false) {
        const storeMapData: StoreMapData = {
          expandedPolygon: undefined,
          standardPolygon: undefined,
          minimumOrderValuePolygons: [],
          marker: undefined,
        };
        if (store.standardDeliveryArea?.length > 0) {
          storeMapData.standardPolygon = this.createImmutablePolygon(
            PolygonType.Standard,
            this.createMappedCoordinates(store.standardDeliveryArea),
          );
          storeMapData.standardPolygon.setMap(this.map!);
        } else {
          conditionallyLog(
            `store ${store.facilityId} is missing standard delivery coordinates`,
            store.standardDeliveryArea,
          );
        }
        if (store.hasExtendedDeliveryArea) {
          if (store.extendedDeliveryArea?.length > 0) {
            storeMapData.expandedPolygon = this.createImmutablePolygon(
              PolygonType.Extended,
              this.createMappedCoordinates(store.extendedDeliveryArea),
            );
            storeMapData.expandedPolygon.setMap(this.map!);
          } else {
            conditionallyLog(
              `store ${store.facilityId} is missing extended delivery coordinates`,
              store.extendedDeliveryArea,
            );
          }
        }
        if (store.facilityCoordinates) {
          storeMapData.marker = this.createStoreMarker(
            store.facilityCoordinates,
            `${store.facilityId} - ${store.facilityName}`,
            false,
          );
        } else {
          conditionallyLog(`store ${store.facilityId} is missing location coordinates`, store.facilityCoordinates);
        }
        if (store.movPolygons.length > 0) {
          store.movPolygons.forEach((polygon) => {
            if (polygon.coordinates.length > 0) {
              const minimumOrderValuePolygon = this.createImmutablePolygon(
                PolygonType.Mov,
                this.createMappedCoordinates(polygon.coordinates),
              );
              storeMapData.minimumOrderValuePolygons?.push(minimumOrderValuePolygon);
              minimumOrderValuePolygon.setMap(this.map!);
            }
          });
        }
        this.otherStores.set(store.facilityId, storeMapData);
      }
    }
    this.removeDeselectedStores(this.otherStores, selectedIds);
  }

  private removeDeselectedStores(stores: Map<number, StoreMapData>, selectedIds: Set<number>): void {
    for (const [facilityId, store] of stores) {
      if (!selectedIds.has(facilityId)) {
        this.toggleStoreMapDataVisibility(store, false);
        stores.delete(facilityId);
      }
    }
  }

  private toggleStoreMapDataVisibility(store: StoreMapData, isVisible: boolean): void {
    store.standardPolygon?.setMap(isVisible ? this.map! : null);
    store.expandedPolygon?.setMap(isVisible ? this.map! : null);
    store.minimumOrderValuePolygons?.forEach((polygon) => {
      polygon.setMap(isVisible ? this.map! : null);
    });
    if (store.marker) {
      store.marker.map = isVisible ? this.map! : null;
    }
  }

  private createMappedCoordinates(coordinates: IDeliveryCoordinates[]): google.maps.LatLng[] {
    return coordinates.map((item) => new google.maps.LatLng(item.latitude, item.longitude));
  }

  public setStandardCoordinates(standardCoordinates: IDeliveryCoordinates[]): void {
    conditionallyLog('setting standard coordinates', standardCoordinates);

    this.queueOrExecute(() => {
      const polygon = this.createPolygon(PolygonType.Standard, this.createMappedCoordinates(standardCoordinates));
      polygon.setMap(this.map!);
      this.standardPolygon?.setMap(null);
      this.standardPolygon = polygon;
      this.lastStandardCoords = standardCoordinates;
      this.wirePolygonEvents(PolygonType.Standard, polygon);

      conditionallyLog('successfully set polygon:', PolygonType.Standard);
    });
  }

  public setExtendedCoordinates(extendedCoordinates: IDeliveryCoordinates[], extendedEnabled: boolean): void {
    conditionallyLog('setting extended coordinates', extendedCoordinates);
    this.extendedDeliveryAreaEnabled = extendedEnabled;
    this.updateMovControlVisibility();

    this.queueOrExecute(() => {
      const polygon = this.createPolygon(PolygonType.Extended, this.createMappedCoordinates(extendedCoordinates));

      if (this.extendedDeliveryAreaEnabled) {
        polygon.setMap(this.map!);
      }
      this.extendedPolygon?.setMap(null);
      this.extendedPolygon = polygon;
      this.lastExtendedCoords = extendedCoordinates;
      this.wirePolygonEvents(PolygonType.Extended, polygon);

      conditionallyLog('successfully set polygon:', PolygonType.Extended);
    });
  }

  public setMovPolygons(movPolygons: MovPolygon[]): void {
    conditionallyLog('setting MOV polygons', movPolygons);

    this.queueOrExecute(() => {
      movPolygons.forEach((movPolygon) => {
        const polygon = this.createPolygon(PolygonType.Mov, this.createMappedCoordinates(movPolygon.coordinates));

        polygon.setMap(this.map!);
        const name = movPolygon.name;
        this.movPolygons.set(name, { polygon, lastCoords: movPolygon.coordinates });
        this.wirePolygonEvents(PolygonType.Mov, polygon, name);
      });
    });
  }

  public setFacilityCoordinates(facilityCoordinates: IDeliveryCoordinates, markerLabel: string): void {
    conditionallyLog('setting facility coordinates', facilityCoordinates);
    this.facilityCoordinates = facilityCoordinates;

    this.queueOrExecute(() => {
      this.applyFacilityCoordinates(facilityCoordinates, markerLabel);
    });
  }

  private applyFacilityCoordinates(facilityCoordinates: IDeliveryCoordinates, markerLabel: string): void {
    this.map!.setCenter(new google.maps.LatLng(facilityCoordinates.latitude, facilityCoordinates.longitude));

    // remove the old maker if one exists
    if (this.facilityMarker) {
      this.facilityMarker.map = null;
    }
    this.facilityMarker = this.createStoreMarker(facilityCoordinates, markerLabel, true);
  }

  public resetZipCodeOverlay(): void {
    if (this.zipOverlayManager) {
      this.zipOverlayManager.toggleOff();
      this.zipcodeOverlayEnabled = false;
      this.zipcodeLabelsEnabled = true;
    }
  }

  public resetZoom(): void {
    conditionallyLog('resetting zoom to default');
    if (this.map) {
      this.map.setZoom(this.zoom);
    }
  }

  public resetPolygons(): void {
    this.standardPolygon?.setMap(null);
    this.extendedPolygon?.setMap(null);
    this.otherStores?.clear();
    this.clearMovPolygons();
  }

  private clearMovPolygons(): void {
    this.movPolygons?.forEach((movPolygon) => movPolygon.polygon.setMap(null));
    this.movPolygons.clear();
  }

  public resetPolygonAroundFacility(): void {
    if (this.facilityCoordinates) {
      const defaultStandardPolygon = PolygonHelpers.generateDefaultStandardPolygon(this.facilityCoordinates);
      const defaultExtendedPolygon = PolygonHelpers.generateDefaultExtendedPolygon(this.facilityCoordinates);

      this.setExtendedCoordinates(defaultExtendedPolygon, this.extendedDeliveryAreaEnabled);
      this.setStandardCoordinates(defaultStandardPolygon);
      this.clearMovPolygons();

      this.deliveryMapChanged.emit({ kind: DeliveryMapChangeKinds.Standard, coordinates: defaultStandardPolygon });
      this.deliveryMapChanged.emit({ kind: DeliveryMapChangeKinds.Extended, coordinates: defaultExtendedPolygon });
      this.deliveryMapChanged.emit({ kind: DeliveryMapChangeKinds.MovAll, movPolygons: [] });
    }
  }

  public saveDeliveryData(): void {
    this.saveButtonClicked.emit();
  }

  public toggleZipCodeLabels(): void {
    this.zipOverlayManager?.toggleLabels(this.zipcodeLabelsEnabled);
  }

  public toggleZipCodeOverlay(): void {
    if (this.zipcodeOverlayEnabled) {
      this.zipOverlayManager.toggleOn();
    } else {
      this.zipOverlayManager.toggleOff();
      this.zipcodeLabelsEnabled = true; // reset back to default
    }
  }

  public createNewMovPolygon(): void {
    conditionallyLog('creating new MOV polygon');

    this.queueOrExecute(() => {
      const newMovPolygonCoordinates = PolygonHelpers.generateDefaultMovPolygon(this.facilityCoordinates!);
      const newMovPolygon = this.createPolygon(PolygonType.Mov, this.createMappedCoordinates(newMovPolygonCoordinates));

      newMovPolygon.setMap(this.map!);
      const name = crypto.randomUUID();
      this.movPolygons.set(name, { polygon: newMovPolygon, lastCoords: newMovPolygonCoordinates });
      this.wirePolygonEvents(PolygonType.Mov, newMovPolygon, name);
    });
  }

  public hasAnyMovPolygons(): boolean {
    return this.movPolygons.size > 0;
  }

  private initializeDeliveryAreaMap(): void {
    const mapProperties = {
      center: new google.maps.LatLng(this.startingLat, this.startingLng),
      zoom: this.zoom,
      mapTypeId: this.mapTypeId,
      mapId: 'delivery-area-map',
    };

    this.map = new google.maps.Map(this.mapElement.nativeElement, mapProperties);

    // Execute any pending operations
    this.pendingOperations.forEach((op) => op());
    this.pendingOperations = [];

    this.measureTool = new MeasureTool(this.map, {
      showSegmentLength: true,
      contextMenu: true,
      unit: MeasureTool.UnitTypeId.IMPERIAL,
    });

    this.zipOverlayManager = new ZipCodeOverlayManager(
      this.map,
      this.storeHandoffService,
      this.toastr,
      this.facilityManager,
    );

    this.createMovControl();
    this.updateMovControlVisibility();
  }

  private createMovControl(): void {
    if (!this.map || this.movControlDiv) {
      return;
    }

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = 'Add MOV Delivery Area';
    btn.classList.add('gm-add-mov-btn');

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.createNewMovPolygon();
    });

    this.map.controls[google.maps.ControlPosition.TOP_RIGHT].push(btn);
    this.movControlDiv = btn;
  }

  private updateMovControlVisibility(): void {
    if (!this.movControlDiv) {
      return;
    }
    const visible = this.extendedDeliveryAreaEnabled && this.editModeEnabled;
    this.movControlDiv.style.display = visible ? 'flex' : 'none';
    this.movControlDiv.title = visible ? 'Add MOV Area' : 'Enable Extended Delivery + Edit Mode to add MOV areas';
    (this.movControlDiv as HTMLButtonElement).disabled = !visible;
  }

  public onEditModeToggle(): void {
    this.applyEditModeToPolygons();
    this.updateMovControlVisibility();
  }

  private handlePolygonRightClick(
    event: google.maps.PolyMouseEvent,
    polygon: google.maps.Polygon,
    polygonType: PolygonType,
  ): void {
    if (event.vertex === undefined) {
      return;
    } else {
      this.removeVertex(event.vertex, polygon, polygonType);
    }
  }

  private removeVertex(vertex: number, polygon: google.maps.Polygon, polygonType: PolygonType): void {
    conditionallyLog('removing vertex number:', vertex);
    const path = polygon.getPath();
    conditionallyLog('current array:', path.getArray());
    if (polygonType === PolygonType.Mov && path.getLength() < 4) {
      this.deleteMovPolygon(polygon);
      return;
    }

    if (path.getLength() < 4 && polygonType !== PolygonType.Mov) {
      this.toastr.error('Delivery area must have a minimum of 3 points');
      return;
    }
    path.removeAt(vertex);
    conditionallyLog('updated array:', path.getArray());
  }

  private deleteMovPolygon(polygon: google.maps.Polygon): void {
    polygon.setMap(null);
    this.movPolygons.forEach((value, key) => {
      if (value.polygon === polygon) {
        conditionallyLog('removing MOV polygon from dictionary:', key);
        this.movPolygons.delete(key);
      }
    });
  }

  private createPolygon(polygonType: PolygonType, mappedCoordinates: google.maps.LatLng[]): google.maps.Polygon {
    const style = this.POLYGON_STYLE[polygonType];
    return new google.maps.Polygon({
      paths: mappedCoordinates,
      strokeColor: style.color,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: style.color,
      fillOpacity: 0.35,
      draggable: this.editModeEnabled,
      geodesic: true,
      editable: this.editModeEnabled,
      zIndex: style.zIndex,
    });
  }

  private createImmutablePolygon(
    polygonType: PolygonType,
    mappedCoordinates: google.maps.LatLng[],
  ): google.maps.Polygon {
    const style = this.POLYGON_STYLE[polygonType];
    return new google.maps.Polygon({
      paths: mappedCoordinates,
      strokeColor: style.color,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: style.color,
      fillOpacity: 0.35,
      draggable: false,
      geodesic: true,
      editable: false,
      zIndex: style.zIndex,
    });
  }

  private createStoreMarker(
    facilityCoordinates: IDeliveryCoordinates,
    markerLabel: string,
    currentSelectedFacility: boolean,
  ): google.maps.marker.AdvancedMarkerElement {
    const pinOptions = currentSelectedFacility
      ? {
          background: '#FF0000',
          borderColor: '#8B0000',
          glyphColor: '#FFFFFF',
          glyphSrc: 'https://maps.gstatic.com/mapfiles/place_api/icons/v2/shoppingcart_pinlet.svg',
        }
      : ({
          background: '#b300ff',
          borderColor: '#4c008b',
          glyphColor: '#FFFFFF',
        } as google.maps.marker.PinElementOptions);
    const container = document.createElement('div');
    container.classList.add('store-marker');

    const label = document.createElement('div');
    label.classList.add(currentSelectedFacility ? 'store-polygon-label-primary' : 'store-polygon-label-other');
    label.textContent = markerLabel;

    const pin = new google.maps.marker.PinElement(pinOptions);

    container.appendChild(label);
    container.appendChild(pin);

    return new google.maps.marker.AdvancedMarkerElement({
      map: this.map,
      position: { lat: facilityCoordinates.latitude, lng: facilityCoordinates.longitude },
      content: container,
    });
  }

  private applyEditModeToPolygons(): void {
    const editable = this.editModeEnabled;

    if (this.standardPolygon) {
      this.standardPolygon.setEditable(editable);
      this.standardPolygon.setDraggable(editable);
    }
    if (this.extendedPolygon) {
      this.extendedPolygon.setEditable(editable);
      this.extendedPolygon.setDraggable(editable);
    }
    this.movPolygons.forEach((entry) => {
      entry.polygon.setEditable(editable);
      entry.polygon.setDraggable(editable);
    });
  }

  private wirePolygonEvents(polygonType: PolygonType, polygon: google.maps.Polygon, name?: string): void {
    conditionallyLog(`"wiring events for ${polygonType} polygon"`, name);
    polygon.addListener('contextmenu', (event: any) => {
      conditionallyLog('right click detected');
      const isVertex = event?.vertex !== undefined;

      if (isVertex) {
        this.handlePolygonRightClick(event, polygon, polygonType);
        this.detectPolygonChange(polygonType, name);
      } else {
        // Not a vertex click, pass the event down so the measure tool can handle it
        this.passEventDown('contextmenu', event);
      }
    });

    const path = polygon.getPath();
    path.addListener('set_at', () => {
      conditionallyLog('path set_at detected (vertex moved)');
      this.detectPolygonChange(polygonType, name);
    });

    path.addListener('insert_at', () => {
      conditionallyLog('path insert_at detected (vertex added)');
      this.detectPolygonChange(polygonType, name);
    });

    path.addListener('remove_at', () => {
      conditionallyLog('path remove_at detected (vertex removed)');
      this.detectPolygonChange(polygonType, name);
    });

    polygon.addListener('click', (event: any) => {
      conditionallyLog('click detected');
      this.detectPolygonChange(polygonType, name);
      this.passEventDown('click', event);
    });

    polygon.addListener('mouseup', (event: any) => {
      conditionallyLog('mouseup detected');
      this.detectPolygonChange(polygonType, name);
      this.passEventDown('mouseup', event);
    });
  }

  private detectPolygonChange(polygonType: PolygonType, name?: string): void {
    switch (polygonType) {
      case PolygonType.Standard:
        this.checkForStandardPolygonChange();
        break;
      case PolygonType.Extended:
        this.checkForExtendedPolygonChange();
        break;
      case PolygonType.Mov:
        if (name) this.checkForMovPolygonChange(name);
        break;
      default:
        break;
    }
  }

  private checkForStandardPolygonChange(): void {
    if (this.standardPolygon === null) {
      conditionallyLog('standard polygon is null, skipping change detection');
      return;
    }
    const currentStandardCoords = this.getCoordinates(this.standardPolygon!);
    if (this.areCoordinatesDifferent(currentStandardCoords, this.lastStandardCoords)) {
      this.lastStandardCoords = currentStandardCoords;
      this.deliveryMapChanged.emit({ kind: DeliveryMapChangeKinds.Standard, coordinates: currentStandardCoords });
    }
  }

  private checkForExtendedPolygonChange(): void {
    if (this.extendedPolygon === null) {
      conditionallyLog('extended polygon is null, skipping change detection');
      return;
    }
    const currentExtendedCoords = this.getCoordinates(this.extendedPolygon!);
    if (this.areCoordinatesDifferent(currentExtendedCoords, this.lastExtendedCoords)) {
      this.lastExtendedCoords = currentExtendedCoords;
      this.deliveryMapChanged.emit({ kind: DeliveryMapChangeKinds.Extended, coordinates: currentExtendedCoords });
    }
  }

  private checkForMovPolygonChange(name: string): void {
    conditionallyLog('checking for MOV polygon change, name:', name);

    const entry = this.movPolygons.get(name);
    if (!entry) {
      this.emitMovPolygons();
      return;
    }

    const current = this.getCoordinates(entry.polygon);
    if (this.areCoordinatesDifferent(current, entry.lastCoords)) {
      entry.lastCoords = current;
      this.emitMovPolygons();
    }
  }

  private emitMovPolygons(): void {
    const movPolygons = Array.from(this.movPolygons.entries()).map(([name, state]) =>
      PolygonHelpers.createMovPolygon(name, state.lastCoords),
    );
    this.deliveryMapChanged.emit({ kind: DeliveryMapChangeKinds.MovAll, movPolygons });
    conditionallyLog('emitting MOV polygon change:', movPolygons);
  }

  private areCoordinatesDifferent(list1: IDeliveryCoordinates[], list2: IDeliveryCoordinates[]): boolean {
    if (list1.length !== list2.length) return true;

    for (let i = 0; i < list1.length; i++) {
      const coord1 = list1[i];
      const coord2 = list2[i];

      if (coord1.latitude !== coord2.latitude || coord1.longitude !== coord2.longitude) {
        return true;
      }
    }

    return false;
  }

  private getCoordinates(polygon: google.maps.Polygon): IDeliveryCoordinates[] {
    return polygon
      .getPath()
      .getArray()
      .map((item) => ({
        latitude: item.lat(),
        longitude: item.lng(),
      }));
  }

  /**
   * Passes the event down to the map. This is required for the measuring tool to work
   * properly when clicking inside the polygon.
   *
   * @param eventName event name to pass down to the map
   * @param event event to pass down to the map
   */
  private passEventDown(eventName: string, event: any): void {
    if (this.map) {
      google.maps.event.trigger(this.map, eventName, event);
    }
  }
}
